import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import ConnectionTable from "../../components/ConnectionTable";
import useAuth from "../../hooks/useAuth";
import { apiRequest } from "../../config/api";
import { FaSyncAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import "./home.css";

const Home = () => {
  const { user, signout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showStripeWarning, setShowStripeWarning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState("");
  const [userData, setUserData] = useState(null);
  const [blockReason, setBlockReason] = useState("");

  // Resolve o ID do promotion_code a partir do código digitado (ex: "LAZARUS30")
  const getPromotionCodeId = async (code) => {
    try {
      if (!code || typeof code !== "string") return null;
      const data = await apiRequest(
        `/stripe/promotion-codes/${encodeURIComponent(code.trim())}`
      );
      return data?.promotionCodeId || null;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 2000; // 2 segundos

    const checkStripe = async (isRetry = false) => {
      if (user?.id) {
        try {
          const data = await apiRequest(
            `/users/${user.id}/subscription-status`
          );

          setUserData(data.data);

          if (data.success && data.data) {
            if (
              (data.data.subscription_status === "active" ||
                data.data.subscription_status === "trialing") &&
              data.data.stripe_id
            ) {
              setBlockReason("");
            } else if (data.data.stripe_id) {
              setBlockReason("regularize");
            } else {
              // Se é um usuário novo (sem stripe_id) e ainda temos tentativas
              if (!isRetry && retryCount < maxRetries) {
                retryCount++;
                setTimeout(() => {
                  checkStripe(true);
                }, retryDelay);
                return;
              }
              setBlockReason("activate");
            }
          } else {
            console.error("❌ Erro ao buscar dados do usuário");
            setBlockReason("activate");
          }
        } catch (err) {
          console.error("❌ Erro na verificação do Stripe:", err);
          setBlockReason("activate");
        }
      }
      setLoading(false);
    };

    checkStripe();
  }, [user]);

  const handleOpenBillingPortal = async () => {
    setStripeLoading(true);
    setStripeError("");
    try {
      const data = await apiRequest("/stripe/billing-portal-session", {
        method: "POST",
        body: JSON.stringify({
          customerId: userData.stripe_id,
        }),
      });

      if (data.success && data.url) {
        window.open(data.url, "_blank");
      } else {
        setStripeError("Erro ao abrir portal de pagamento. Tente novamente.");
      }
    } catch (err) {
      setStripeError("Erro ao abrir portal de pagamento.");
    } finally {
      setStripeLoading(false);
    }
  };

  if (loading) {
    return <div>{t("home.loading")}</div>;
  }

  const isBlocked = blockReason === "activate" || blockReason === "regularize";

  return (
    <div className="home-container" style={{ position: "relative" }}>
      {blockReason === "activate" && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              width: "100vw",
              height: "100vh",
              backdropFilter: "blur(8px)",
              background: "rgba(34, 41, 55, 0.55)",
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 1001,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1002,
              pointerEvents: "auto",
              background: "#23272f",
              color: "#fff",
              padding: "1.5rem",
              borderRadius: 8,
              maxWidth: 420,
              textAlign: "center",
              boxShadow: "0 2px 16px rgba(0,0,0,0.25)",
            }}
          >
            <button
              onClick={() => window.location.reload()}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#43856D",
                fontSize: 22,
                padding: 0,
              }}
              title="Atualizar página"
            >
              <FaSyncAlt />
            </button>
            <h2 style={{ marginBottom: 12, color: "#43856D" }}>
              {t("home.activateTitle")}
            </h2>
            <p
              style={{ marginBottom: 18 }}
              dangerouslySetInnerHTML={{ __html: t("home.activateMsg") }}
            />
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={signout}
                style={{
                  background: "#555555",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 18px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {t("home.signout")}
              </button>
              <button
                onClick={async () => {
                  try {
                    setStripeLoading(true);
                    setStripeError("");

                    const data = await apiRequest(
                      "/stripe/create-checkout-session",
                      {
                        method: "POST",
                        body: JSON.stringify({
                          email: userData?.email || "",
                          couponCode: userData?.coupon_code_used || "",
                          referalId: userData?.referal_id_used || "",
                        }),
                      }
                    );

                    if (data.success && data.url) {
                      window.open(data.url, "_blank");
                    } else {
                      setStripeError(
                        "Erro ao criar sessão de assinatura. Tente novamente."
                      );
                    }
                  } catch (err) {
                    setStripeError("Erro ao criar sessão de assinatura.");
                  } finally {
                    setStripeLoading(false);
                  }
                }}
                style={{
                  background: "#43856D",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 18px",
                  fontWeight: 500,
                  cursor: stripeLoading ? "not-allowed" : "pointer",
                  opacity: stripeLoading ? 0.7 : 1,
                }}
                disabled={stripeLoading}
              >
                {stripeLoading ? t("home.loading") : t("home.activateBtn")}
              </button>
            </div>
            {stripeError && (
              <div style={{ color: "#ef4444", marginTop: 12, fontWeight: 500 }}>
                {stripeError}
              </div>
            )}
          </div>
        </div>
      )}

      {blockReason === "regularize" && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              width: "100vw",
              height: "100vh",
              backdropFilter: "blur(8px)",
              background: "rgba(34, 41, 55, 0.55)",
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 1001,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1002,
              pointerEvents: "auto",
              background: "#23272f",
              color: "#fff",
              padding: "1.5rem",
              borderRadius: 8,
              maxWidth: 420,
              textAlign: "center",
              boxShadow: "0 2px 16px rgba(0,0,0,0.25)",
            }}
          >
            <button
              onClick={() => window.location.reload()}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#43856D",
                fontSize: 22,
                padding: 0,
              }}
              title="Atualizar página"
            >
              <FaSyncAlt />
            </button>
            <h2 style={{ marginBottom: 12, color: "#ef4444" }}>
              {t("home.regularizeTitle")}
            </h2>
            <p style={{ marginBottom: 18 }}>{t("home.regularizeMsg")}</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={signout}
                style={{
                  background: "#555555",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 18px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {t("home.signout")}
              </button>
              <button
                onClick={handleOpenBillingPortal}
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 18px",
                  fontWeight: 500,
                  cursor: stripeLoading ? "not-allowed" : "pointer",
                  opacity: stripeLoading ? 0.7 : 1,
                }}
                disabled={stripeLoading}
              >
                {stripeLoading ? t("home.loading") : t("home.regularizeBtn")}
              </button>
            </div>
            {stripeError && (
              <div style={{ color: "#ef4444", marginTop: 12, fontWeight: 500 }}>
                {stripeError}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        style={
          isBlocked
            ? {
                filter: "blur(8px)",
                pointerEvents: "none",
                userSelect: "none",
                position: "relative",
                zIndex: 0,
              }
            : {}
        }
        tabIndex={-1}
        aria-hidden={isBlocked ? "true" : undefined}
      >
        <Header />

        <main className="main-content">
          <div className="container">
            <ConnectionTable />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Home;
